import type { LiveTeamPlayer } from './types';

/**
 * What we know about whether a pick will contribute points this gameweek.
 * - `played`: has recorded minutes, so it counts and can come off the bench
 * - `blank`: confirmed it will not play, because its match finished with no
 *   minutes or Opta left it out of the matchday squad
 * - `pending`: still could play, so nothing is decided yet
 */
export type PickAvailability = 'played' | 'blank' | 'pending';

type Position = LiveTeamPlayer['position'];

/**
 * The minimum a pick needs for substitution resolution. Structural, so both
 * the live team's picks and squads synthesised from other payloads (a
 * mini-league leaderboard entry, say) can be resolved by the same rules.
 */
export type AutoSubPick = {
  /** Squad slot, the one identifier guaranteed unique across the fifteen. */
  slot: number;
  isStarter: boolean;
  position: Position;
  multiplier: number;
  isCaptain: boolean;
  isViceCaptain: boolean;
};

const BENCH_BOOST = 'bboost';

// A valid XI is exactly one keeper plus the outfield minimums. FPL rejects a
// substitution that would break these, which is why a blank defender is not
// always replaceable even with players left on the bench.
//
// The backend enumerates the eight legal formations rather than testing
// minimums (VALID_FORMATIONS in ffh-public-api's shared/contracts/fpl-rules).
// The two agree: a squad is always 2 GK / 5 DEF / 5 MID / 3 FWD, and every
// split of the ten outfield places that clears these minimums without
// exceeding those squad counts is one of the eight. Checking minimums is the
// same rule expressed against the eleven we actually hold.
const GOALKEEPER_COUNT = 1;
const MINIMUM_BY_POSITION: Record<Exclude<Position, 'GK'>, number> = {
  DEF: 3,
  MID: 2,
  FWD: 1
};

const isValidFormation = (positions: Position[]): boolean => {
  const counts = { GK: 0, DEF: 0, MID: 0, FWD: 0 };
  for (const position of positions) counts[position] += 1;
  return (
    counts.GK === GOALKEEPER_COUNT &&
    counts.DEF >= MINIMUM_BY_POSITION.DEF &&
    counts.MID >= MINIMUM_BY_POSITION.MID &&
    counts.FWD >= MINIMUM_BY_POSITION.FWD
  );
};

/** A bench player taking a blanked starter's place in the XI. */
export type Substitution = {
  outSlot: number;
  inSlot: number;
};

export type EffectiveLineup = {
  /**
   * The multiplier each pick actually scores at, keyed by squad slot, the one
   * identifier guaranteed unique across the fifteen. A starter that was
   * substituted out drops to 0 and the bench player replacing it comes in at 1;
   * everyone else keeps the multiplier the team was set up with.
   */
  multipliers: Map<number, number>;
  /** Swaps that were applied, so the pitch can show the XI that is counting. */
  substitutions: Substitution[];
  /** Slot wearing the armband once a blanked captain has handed it on. */
  captainSlot: number | null;
};

/**
 * The substitutions FPL makes on a bench that has finished playing, in the
 * order it works through them: each blanked starter takes the first reserve who
 * has minutes and whose introduction leaves a legal formation.
 */
const resolveSettledSubstitutions = (
  starters: AutoSubPick[],
  bench: AutoSubPick[],
  availability: Map<number, PickAvailability>
): Substitution[] => {
  const substitutions: Substitution[] = [];
  const usedSubstitutes = new Set<number>();
  // The XI is eleven slots; a substitution swaps the position occupying one of
  // them, and the result still has to be a legal formation.
  const xiPositions = starters.map((pick) => pick.position);

  starters.forEach((starter, index) => {
    if (availability.get(starter.slot) !== 'blank') return;

    const replacement = bench.find((candidate) => {
      if (usedSubstitutes.has(candidate.slot)) return false;
      if (availability.get(candidate.slot) !== 'played') return false;
      // Keepers only ever swap with the bench keeper, in both directions.
      if ((starter.position === 'GK') !== (candidate.position === 'GK')) {
        return false;
      }
      const next = [...xiPositions];
      next[index] = candidate.position;
      return isValidFormation(next);
    });

    if (!replacement) return;

    usedSubstitutes.add(replacement.slot);
    xiPositions[index] = replacement.position;
    substitutions.push({ outSlot: starter.slot, inSlot: replacement.slot });
  });

  return substitutions;
};

const isSameSwap = (a: Substitution, b: Substitution) =>
  a.outSlot === b.outSlot && a.inSlot === b.inSlot;

/**
 * The substitutions that hold whatever the unresolved reserves go on to do.
 *
 * FPL works the bench in the order the manager set, so a reserve whose fixture
 * has not settled can still claim a vacancy ahead of everyone behind him.
 * Promoting one of them in the meantime is what put a later reserve on the
 * pitch while mini-leagues, which reads FPL's own resolution, showed the first
 * (FFH-3129).
 *
 * Holding every swap behind him goes too far the other way, though: with two or
 * more blanked starters some swaps come out the same however he turns out, and
 * withholding those leaves a reserve who is already counting off the pitch and
 * out of the running total. So each way the unresolved reserves could go is
 * resolved in full, and only the swaps common to every one of them are applied.
 * The bench is four players, so that is at most sixteen passes over eleven
 * slots.
 */
const resolveSubstitutions = (
  starters: AutoSubPick[],
  bench: AutoSubPick[],
  availability: Map<number, PickAvailability>
): Substitution[] => {
  const unresolved = bench.filter(
    (pick) => availability.get(pick.slot) === 'pending'
  );
  if (unresolved.length === 0) {
    return resolveSettledSubstitutions(starters, bench, availability);
  }

  let common: Substitution[] | undefined;
  for (let outcome = 0; outcome < 1 << unresolved.length; outcome += 1) {
    const settled = new Map(availability);
    unresolved.forEach((pick, index) => {
      settled.set(pick.slot, (outcome >> index) & 1 ? 'played' : 'blank');
    });

    const resolved = resolveSettledSubstitutions(starters, bench, settled);
    common =
      common === undefined
        ? resolved
        : common.filter((swap) =>
            resolved.some((other) => isSameSwap(swap, other))
          );
    if (common.length === 0) return [];
  }

  return common ?? [];
};
/**
 * Resolves who is counting, at what multiplier, applying auto-subs and the
 * vice-captaincy the way FPL does.
 *
 * Ported from scoreline-app's `screens/live/model/auto-subs.ts` so both clients
 * compute the running score by the same rules. Divergence here is the exact
 * defect the original ticket was raised for, where one surface showed 22 points
 * and another 33 for the same team.
 */
export const resolveEffectiveMultipliers = (
  picks: AutoSubPick[],
  availability: Map<number, PickAvailability>,
  activeChip: string | null
): EffectiveLineup => {
  const isBenchBoostActive = activeChip === BENCH_BOOST;
  const bySlot = [...picks].sort((a, b) => a.slot - b.slot);
  const starters = bySlot.filter((pick) => pick.isStarter);
  const bench = bySlot.filter((pick) => !pick.isStarter);

  const multipliers = new Map<number, number>(
    bySlot.map((pick) => [
      pick.slot,
      isBenchBoostActive && !pick.isStarter ? 1 : pick.multiplier
    ])
  );

  // Bench boost scores all fifteen, so there is no bench to substitute from and
  // nothing a substitution could add.
  const substitutions = isBenchBoostActive
    ? []
    : resolveSubstitutions(starters, bench, availability);

  for (const { outSlot, inSlot } of substitutions) {
    multipliers.set(outSlot, 0);
    multipliers.set(inSlot, 1);
  }

  // The vice-captain inherits the armband when the captain does not play, but
  // only if they are counting themselves. A vice-captain sitting on an unsubbed
  // bench captains nothing.
  //
  // Being blank disqualifies them whatever the multiplier says. A blanked
  // starter with no legal replacement keeps its multiplier of 1, and under
  // bench boost so does a blanked bench player, so the multiplier alone would
  // hand the armband to a player who never took the pitch. FPL only applies the
  // vice-captaincy to a vice-captain who does. `pending` still qualifies: they
  // have no points yet either way, and settling it early is what moves the
  // armband as soon as the captain's absence is known.
  const captain = bySlot.find((pick) => pick.isCaptain);
  const viceCaptain = bySlot.find((pick) => pick.isViceCaptain);
  let captainSlot = captain?.slot ?? null;
  if (captain && viceCaptain && availability.get(captain.slot) === 'blank') {
    const isViceCounting =
      (multipliers.get(viceCaptain.slot) ?? 0) >= 1 &&
      availability.get(viceCaptain.slot) !== 'blank';
    if (isViceCounting) {
      multipliers.set(viceCaptain.slot, captain.multiplier);
      captainSlot = viceCaptain.slot;
    }
  }

  return { multipliers, substitutions, captainSlot };
};

/**
 * Reorders a squad to show the XI that is counting: each substituted-out
 * starter drops to the bench slot of the substitute who replaced them, and the
 * substitute takes the starter's place. Array order is preserved, so callers
 * can keep rendering the starting and bench lists positionally.
 */
export const applySubstitutions = <T extends { slot: number }>(
  starters: T[],
  bench: T[],
  substitutions: Substitution[]
): { starting: T[]; bench: T[] } => {
  const bySlot = new Map<number, T>(
    [...starters, ...bench].map((pick) => [pick.slot, pick])
  );
  const cameOnForStarter = new Map(
    substitutions.map(({ outSlot, inSlot }) => [outSlot, inSlot])
  );
  const wentOffForSub = new Map(
    substitutions.map(({ outSlot, inSlot }) => [inSlot, outSlot])
  );

  const occupantOf = (pick: T, swaps: Map<number, number>): T => {
    const swappedSlot = swaps.get(pick.slot);
    return swappedSlot === undefined ? pick : bySlot.get(swappedSlot) ?? pick;
  };

  return {
    starting: starters.map((starter) => occupantOf(starter, cameOnForStarter)),
    bench: bench.map((substitute) => occupantOf(substitute, wentOffForSub))
  };
};
